import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { getCoach, updateCoachProfile } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { getCoachAttendanceGradeLabel, getCoachStatusLabel, getCoachOverallGradeLabel } from '@/lib/coach-utils';
import { coachGradeSchema } from '@airreading/common';
import {
  coachAttendanceGradeOptions,
  coachAttendanceGradeSchema,
  coachOverallGradeOptions,
  coachOverallGradeSchema,
  CoachStatus,
  CoachStatusTree,
  CoachSubStatus,
  CoachSubStatusLabelMap,
  FullCoach,
  teachingGradeOptions,
} from '@airreading/common/data';

import { useToast } from '@/components/ui/hooks/use-toast';
import { InfoKeyValue } from '@/components/info-key-value';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { EditableInfoCard, SubmitProps } from '@/components/editable-info-card';
import { FormInputField } from '@/components/forms/form-input-field';
import { CoachStatusMenu } from './coach-status-menu';
import { CoachPauseReassignmentModal } from './coach-pause-reassignment-modal';
import { ArrowTooltip } from '@/components/arrow-tooltip';
import { Info } from 'lucide-react';

const pauseReassignmentTriggerSubStatuses: CoachSubStatus[] = [
  CoachSubStatus.non_renewal_of_contract,
  CoachSubStatus.offboard_requested,
];

function hasPauseReassignmentTrigger(status: CoachStatus | undefined, subStatuses: CoachSubStatus[]): boolean {
  return (
    status === CoachStatus.pause &&
    subStatuses.some((subStatus) => pauseReassignmentTriggerSubStatuses.includes(subStatus))
  );
}

const subStatusesByStatusSchema = z.record(z.nativeEnum(CoachStatus), z.array(z.nativeEnum(CoachSubStatus)));

const editTeachingFormSchema = z.object({
  status: z.nativeEnum(CoachStatus).optional(),
  subStatusesByStatus: subStatusesByStatusSchema.default({}),
  qualityGrade: coachGradeSchema.optional(),
  studentGroupCount: z.number().optional(),
  overallGrade: coachOverallGradeSchema.optional(),
  attendanceGrade: coachAttendanceGradeSchema.optional(),
  tnsCountPast15Days: z.number().optional(),
  subRequestCountPast15Days: z.number().optional(),
});

export type EditTeachingFormSchema = z.infer<typeof editTeachingFormSchema>;

export interface TeachingSectionCardProps {
  coach: FullCoach;
  userId: string;
}

export function TeachingSectionCard({ coach, userId }: TeachingSectionCardProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync: updateCoachProfileMutate } = useMutation({ mutationFn: updateCoachProfile });
  const [pauseReassignmentModal, setPauseReassignmentModal] = useState<{
    pauseSubStatuses: CoachSubStatus[];
  } | null>(null);

  const onSubmit = async ({ formData, disableEditing }: SubmitProps<EditTeachingFormSchema>) => {
    try {
      const { status, subStatusesByStatus, qualityGrade } = formData;
      const subStatuses = status ? subStatusesByStatus?.[status] ?? [] : [];

      // If the status has children, it must select at least one
      if (status && subStatuses.length === 0 && CoachStatusTree[status].length) {
        toast({
          title: `Please select at least one reason for the status "${getCoachStatusLabel(status)}"`,
        });
        return;
      }

      const previouslyTriggered = hasPauseReassignmentTrigger(coach.status ?? undefined, coach.subStatuses ?? []);
      const nextTriggered = hasPauseReassignmentTrigger(status, subStatuses);

      await updateCoachProfileMutate({
        userId,
        data: {
          status,
          subStatuses,
          qualityGrade,
        },
      });

      toast({ title: 'Teaching info updated' });
      disableEditing();
      await queryClient.invalidateQueries(getCoach(userId));

      const activeGroupCount = coach.studentGroupCount ?? 0;
      if (nextTriggered && !previouslyTriggered && activeGroupCount > 0) {
        setPauseReassignmentModal({
          pauseSubStatuses: subStatuses.filter((subStatus) => pauseReassignmentTriggerSubStatuses.includes(subStatus)),
        });
      }
    } catch (e) {
      toast({
        title: airServerErrorFrom(e)?.userFacingMessage || 'Failed to update teaching info',
      });
    }
  };

  const currentStatus = coach.status ?? undefined;
  const qualityGrade = coach.qualityGrade ?? undefined;
  const attendanceGrade = coach.attendanceGrade ?? undefined;
  const overallGrade = coach.overallGrade ?? undefined;

  return (
    <>
      {pauseReassignmentModal && (
        <CoachPauseReassignmentModal
          coachId={userId}
          pauseSubStatuses={pauseReassignmentModal.pauseSubStatuses}
          onClose={() => setPauseReassignmentModal(null)}
        />
      )}
      <EditableInfoCard
        headerText="Teaching"
        formSchema={editTeachingFormSchema}
        onSubmit={onSubmit}
        defaultValues={{
          status: currentStatus,
          subStatusesByStatus: currentStatus ? { [currentStatus]: coach.subStatuses ?? [] } : {},
          qualityGrade,
          studentGroupCount: coach.studentGroupCount ?? 0,
          overallGrade,
          attendanceGrade,
          tnsCountPast15Days: coach.tnsCountPast15Days ?? 0,
          subRequestCountPast15Days: coach.subRequestCountPast15Days ?? 0,
        }}
      >
        {({ form, editing }) => (
          <>
            <InfoKeyValue
              label="Status"
              value={
                <div className="flex gap-1 items-center">
                  {getCoachStatusLabel(currentStatus)}
                  {coach.subStatuses && coach.subStatuses.length > 0 && (
                    <ArrowTooltip trigger={<Info className="h-5 w-5" />} position="right">
                      {coach.subStatuses.map((ss) => (
                        <div key={ss} className="text-left">
                          {CoachSubStatusLabelMap[ss]}
                        </div>
                      ))}
                    </ArrowTooltip>
                  )}
                </div>
              }
              edit={{
                editing,
                editModeComponent: <CoachStatusMenu label="Status" form={form} editing={editing} />,
              }}
            />
            <InfoKeyValue
              label="Number of active classes"
              value={coach.studentGroupCount ?? 0}
              edit={{
                editing,
                editModeComponent: (
                  <FormInputField
                    name="studentGroupCount"
                    label="Number of active classes"
                    form={form}
                    inputProps={{ disabled: true }}
                  />
                ),
              }}
            />
            <InfoKeyValue
              label="Overall grade"
              value={overallGrade ? getCoachOverallGradeLabel(overallGrade) : '-'}
              edit={{
                editing,
                editModeComponent: (
                  <FormSingleSelect
                    name="overallGrade"
                    label="Overall grade"
                    form={form}
                    options={coachOverallGradeOptions}
                    renderOption={(grade) => getCoachOverallGradeLabel(grade)}
                    triggerProps={{ className: 'w-2/3' }}
                    selectProps={{ disabled: true }}
                  />
                ),
              }}
            />
            <InfoKeyValue
              label="Teaching grade"
              value={qualityGrade ?? '-'}
              edit={{
                editing,
                editModeComponent: (
                  <FormSingleSelect
                    name="qualityGrade"
                    label="Teaching grade"
                    form={form}
                    options={teachingGradeOptions}
                    renderOption={(grade) => grade}
                    triggerProps={{ className: 'w-2/3' }}
                  />
                ),
              }}
            />
            <InfoKeyValue
              label="Attendance grade"
              value={getCoachAttendanceGradeLabel(attendanceGrade)}
              edit={{
                editing,
                editModeComponent: (
                  <FormSingleSelect
                    name="attendanceGrade"
                    label="Attendance grade"
                    form={form}
                    options={coachAttendanceGradeOptions}
                    renderOption={(grade) => grade}
                    triggerProps={{ className: 'w-2/3' }}
                    selectProps={{ disabled: true }}
                  />
                ),
              }}
            />
            <InfoKeyValue
              label="Total TNS - past 15 days"
              value={coach.tnsCountPast15Days ?? 0}
              edit={{
                editing,
                editModeComponent: (
                  <FormInputField
                    name="tnsCountPast15Days"
                    label="Total TNS - past 15 days"
                    form={form}
                    inputProps={{ disabled: true }}
                  />
                ),
              }}
            />
            <InfoKeyValue
              label="Total sub requests - past 15 days"
              value={coach.subRequestCountPast15Days ?? 0}
              edit={{
                editing,
                editModeComponent: (
                  <FormInputField
                    name="subRequestCountPast15Days"
                    label="Total sub requests - past 15 days"
                    form={form}
                    inputProps={{ disabled: true }}
                  />
                ),
              }}
            />
          </>
        )}
      </EditableInfoCard>
    </>
  );
}
